export const convertToString = (value: string | number) => {
  return typeof value === 'number' ? value.toString() : value;
};

interface FormatCurrencyOptions {
  compact?: boolean;
  currency?: string;
  maximumFractionDigits?: number;
}

export const formatCurrency = (medianWage: string | null | undefined, { compact, currency = 'USD', maximumFractionDigits }: FormatCurrencyOptions = {}): string => {
  let wage: number | undefined;

  if (typeof medianWage === 'string') {
    wage = Number.parseFloat(medianWage);
  }

  if (wage !== undefined && !Number.isNaN(wage)) {
    if (compact) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        notation: 'compact',
        maximumFractionDigits: 0,
      }).format(wage);
    }

    let formattedWage = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits,
    }).format(wage);

    // Remove trailing .00 if present
    if (formattedWage.endsWith('.00')) {
      formattedWage = formattedWage.replace(/\.00$/, '');
    }

    return formattedWage;
  }

  return 'N/A';
};

/**
 * A yearly wage to the nearest hundred dollars: the figures are medians and averages of medians,
 * so the last two digits (and the cents the US national figure carries) are noise at this scale.
 */
export const formatWage = (wage: string): string => {
  const parsed = Number.parseFloat(wage);
  if (Number.isNaN(parsed)) {
    return formatCurrency(wage);
  }
  return formatCurrency(String(Math.round(parsed / 100) * 100), { maximumFractionDigits: 0 });
};

export const formatWageString = (wage?: string | null): string => {
  if (!wage || Number(wage) <= 0) {
    return '';
  }
  const currencyString = formatWage(wage);
  if (currencyString === 'N/A') {
    return '';
  }

  return `${currencyString}/year`;
};

export const toWagePerYear = (val: string | null | undefined): string => {
  if (!val) {
    return 'N/A';
  }
  const formatted = formatWage(val);
  return formatted === 'N/A' ? 'N/A' : `${formatted} Avg/yr`;
};

export const generateValidUrl = (urlString: string) => {
  let validUrl = urlString;
  // Check if the string already contains "http" or "https", to avoid broken links
  if (!validUrl.startsWith('http://') && !validUrl.startsWith('https://')) {
    // If not, prepend "https://" to the string

    validUrl = `https://${validUrl}`;
  }

  // Create a URL object for validation and correction
  try {
    const url = new URL(validUrl);
    return url.href; // Return the validated URL
  }
  // eslint-disable-next-line unused-imports/no-unused-vars
  catch (e) {
    // Handle invalid URLs or potential errors
    return null;
  }
};

export const arraysEqual = <T>(a: T[] | undefined | null, b: readonly T[] | undefined | null): boolean => {
  if (!a && !b) {
    return true;
  }
  if (!a) {
    return b?.length === 0;
  }
  if (!b) {
    return a.length === 0;
  }
  if (a.length !== b.length) {
    return false;
  }
  const setB = new Set(b);
  return a.every(val => setB.has(val));
};
